import type { ConvoAIErrorMessage } from '../api/ConvoAI/ConvoAIStreamingModels';
import type {
	AnswerPartResponseMessage,
	ChannelIdResponseMessage,
	ErrorResponseMessage,
	FinalResponseMessage,
	FollowUpObject,
	FollowUpResponseMessage,
	StreamError,
	TraceResponseMessage,
} from '../api/ConvoAI/ConvoAIStreamMessages';

export enum ConvoAIResponseState {
	Initialization = 'Initialization',
	ChannelId = 'ChannelId',
	AnswerPart = 'AnswerPart',
	FinalResponse = 'FinalResponse',
	FollowUp = 'FollowUp',
	Error = 'Error',
	Trace = 'Trace',
}

export type ConvoAIStreamingState<
	TFinalResponse extends FinalResponseMessage = FinalResponseMessage<string>,
	TError extends ErrorResponseMessage = ErrorResponseMessage<`${ConvoAIErrorMessage}`>,
	TFollowUp extends FollowUpResponseMessage = FollowUpResponseMessage<FollowUpObject[]>,
	TAnswerPart extends AnswerPartResponseMessage = TFinalResponse,
	TChannelId extends ChannelIdResponseMessage = TFinalResponse,
	TTrace extends TraceResponseMessage = TraceResponseMessage,
> =
	| {
			answerChunkIndex?: number;
			content: null;
			error?: StreamError<TError['message_template']>;
			followUpContent?: TFollowUp['follow_ups'];
			responseState: ConvoAIResponseState.Initialization;
			traceContent?: TTrace['content'] | null;
	  }
	| {
			answerChunkIndex?: number;
			content: TChannelId['content'];
			error?: StreamError<TError['message_template']>;
			followUpContent?: TFollowUp['follow_ups'];
			responseState: ConvoAIResponseState.ChannelId;
			traceContent?: TTrace['content'] | null;
	  }
	| {
			answerChunkIndex: number;
			content: TAnswerPart['content'];
			error?: StreamError<TError['message_template']>;
			followUpContent?: TFollowUp['follow_ups'];
			responseState: ConvoAIResponseState.AnswerPart;
			traceContent?: TTrace['content'] | null;
	  }
	| {
			answerChunkIndex?: number;
			content: TFinalResponse['content'];
			error?: StreamError<TError['message_template']>;
			followUpContent?: TFollowUp['follow_ups'];
			responseState: ConvoAIResponseState.FinalResponse;
			traceContent?: TTrace['content'] | null;
	  }
	| {
			answerChunkIndex?: number;
			content: TFinalResponse['content'] | null;
			error?: StreamError<TError['message_template']>;
			followUpContent: TFollowUp['follow_ups'];
			responseState: ConvoAIResponseState.FollowUp;
			traceContent?: TTrace['content'] | null;
	  }
	| {
			answerChunkIndex?: number;
			content: TFinalResponse['content'] | null;
			error?: StreamError<TError['message_template']>;
			followUpContent?: TFollowUp['follow_ups'];
			responseState: ConvoAIResponseState.Trace;
			traceContent: TTrace['content'] | null;
	  }
	| {
			answerChunkIndex?: number;
			content: TFinalResponse['content'] | null;
			error: StreamError<TError['message_template']>;
			followUpContent?: TFollowUp['follow_ups'];
			responseState: ConvoAIResponseState.Error;
			traceContent?: TTrace['content'] | null;
	  };

export function getDefaultConvoAIStreamingState<
	TFinalResponse extends FinalResponseMessage = FinalResponseMessage<string>,
	TError extends ErrorResponseMessage = ErrorResponseMessage<`${ConvoAIErrorMessage}`>,
	TFollowUp extends FollowUpResponseMessage = FollowUpResponseMessage<FollowUpObject[]>,
	TAnswerPart extends AnswerPartResponseMessage = TFinalResponse,
	TChannelId extends ChannelIdResponseMessage = TFinalResponse,
	TTrace extends TraceResponseMessage = TraceResponseMessage,
>(): ConvoAIStreamingState<TFinalResponse, TError, TFollowUp, TAnswerPart, TChannelId, TTrace> {
	return {
		content: null,
		responseState: ConvoAIResponseState.Initialization,
	};
}
